import axios from 'axios';
import { APIConfiguration, axiosMYLAB_V2 } from 'api';
import { AppointmentMedicalAct } from 'models/appointment.medical.act.model';
import { Appointment } from 'models/appointment.model';
import { AppointmentSlots } from 'models/appointment.slots.model';
import { convertDate } from 'utils/func/Date.func';
import { BaseService } from './base.service';

/**
 * V2 Appointment Service - calls new myrdv-backend endpoints via mylab-backend proxy
 * Adapts new API response formats to match old format so UI components work unchanged.
 */
class AppointmentV2Service extends BaseService {
  /**
   * Get medical acts (analyses) from new API
   * New API: GET /api/v4/{lang}/external/myrdv/analyse/get-all?lang={lang}
   * Adapts Analyse entity → AppointmentMedicalAct format
   */
  async getMedicalActs() {
    const lang = this.getLang();
    const { data } = await axiosMYLAB_V2.get<any[]>(
      this.apiConfig.APPOINTMENTS_V2.GET_MEDICAL_ACTS(lang),
    );

    this.log(`[V2] getMedicalActs()`);

    // Adapt new Analyse format → old AppointmentMedicalAct format
    return data.map(
      (analyse: any): AppointmentMedicalAct => ({
        id: analyse.id,
        internal: false,
        additional: false,
        constkey: analyse.code ?? '',
        name: analyse.name ?? '',
        description: analyse.description ?? '',
        duration: analyse.duration_act ?? analyse.patient_duration ?? 0,
        places: [],
      }),
    );
  }

  /**
   * Get available slots from new API
   * New API: POST /api/v4/{lang}/external/myrdv/appointment/get-appointment-available
   * Request: { start, end, loc: [lon, lat], analyses: number[], patientsQuantity }
   * Response: { "2024-02-02": [{ start, end, available }] }
   * Adapts → old AppointmentSlots format
   */
  async getSlots(
    dateFrom: Date,
    dateTo: Date,
    medicalActsIds: string[],
    addressZipCode: string,
    addressCountryIso: string,
  ) {
    const lang = this.getLang();

    // Geocode the address zip code + country to get real coordinates
    let coord = [6.1296, 49.8153]; // fallback: Luxembourg center
    try {
      const geo = await axios.get('https://nominatim.openstreetmap.org/search', {
        params: {
          postalcode: addressZipCode,
          country: addressCountryIso || 'LU',
          format: 'json',
          limit: 1,
        },
      });
      if (geo.data?.length > 0) {
        coord = [parseFloat(geo.data[0].lon), parseFloat(geo.data[0].lat)];
      }
    } catch {
      // Use fallback coordinates
    }

    const { data } = await axiosMYLAB_V2.post<Record<string, { start: string; end: string; available: boolean }[]>>(
      this.apiConfig.APPOINTMENTS_V2.GET_SLOTS(lang),
      {
        start: convertDate(dateFrom.toISOString(), false, "yyyy-MM-dd'T'HH:mm:ss'Z'"),
        end: convertDate(dateTo.toISOString(), false, "yyyy-MM-dd'T'HH:mm:ss'Z'"),
        loc: coord,
        analyses: medicalActsIds.map((id) => parseInt(id)),
        patientsQuantity: 1,
      },
    );

    this.log(`[V2] getSlots()`);

    // Adapt new format → old AppointmentSlots format
    const slots: { [date: string]: string[] } = {};
    let slotDuration = 'PT00H30M'; // default

    Object.entries(data).forEach(([date, dateSlots]) => {
      const availableSlots = dateSlots.filter((s) => s.available);
      if (availableSlots.length > 0) {
        slots[date] = availableSlots.map((s) => {
          const startTime = new Date(s.start);
          return `${startTime.getHours()}:${startTime.getMinutes().toString().padStart(2, '0')}`;
        });

        // Estimate slot duration from first available slot
        if (availableSlots[0]) {
          const start = new Date(availableSlots[0].start);
          const end = new Date(availableSlots[0].end);
          const diffMinutes = Math.round((end.getTime() - start.getTime()) / 60000);
          const hours = Math.floor(diffMinutes / 60);
          const minutes = diffMinutes % 60;
          slotDuration = `PT${hours.toString().padStart(2, '0')}H${minutes.toString().padStart(2, '0')}M`;
        }
      }
    });

    const result: AppointmentSlots = {
      pandemic: false,
      slot_duration: slotDuration,
      slot_step_duration: slotDuration,
      slots,
    };

    return result;
  }

  /**
   * Book/create an appointment via new API
   * New API: POST /api/v4/{lang}/external/myrdv/appointment/create
   * Request format: { slot, prescriptions: [{ patientId, analyses, ordonnances }], locale, coordInfo }
   */
  async bookAppointment(appointment: Appointment) {
    const lang = this.getLang();

    // Fetch medical acts to map IDs → codes
    const allMedicalActs = await this.getMedicalActs();
    const codeById = new Map(allMedicalActs.map((a) => [a.id.toString(), a.constkey]));

    // Adapt old Appointment format → new CreateAppointment format
    const prescriptions = appointment.patients.map((patient) => ({
      analyses: patient.medical_acts.map((id) => ({ code: codeById.get(id) || id })),
      ordonnances: patient.prescriptions?.map((p: any) => (typeof p === 'string' ? p : p.file_content)) ?? [],
      patient: {
        id: patient.patient_id ? parseInt(patient.patient_id) : undefined,
        mylabId: patient.patient_id ? parseInt(patient.patient_id) : undefined,
        firstname: patient.first_name || undefined,
        lastname: patient.last_name || undefined,
        birthname: patient.birth_name || undefined,
        birthdate: patient.birth_date ? String(patient.birth_date) : null,
        cns: patient.cns || undefined,
        gender: patient.gender || undefined,
        email: patient.email || undefined,
        phone: patient.phone || patient.mobile_phone || undefined,
        marital_name: patient.last_name || '',
        health_fund_id: '',
      },
    }));

    // Convert slot from local time string to UTC ISO format
    // appointment.slot comes as "yyyy-MM-dd HH:mm" in local timezone
    const slotParts = appointment.slot.match(/(\d{4}-\d{2}-\d{2})\s+(\d{2}:\d{2})/);
    let slotISO = appointment.slot;
    if (slotParts) {
      // Create Date in local timezone (no 'Z' suffix = local)
      const localDate = new Date(`${slotParts[1]}T${slotParts[2]}:00`);
      slotISO = localDate.toISOString(); // converts to UTC
    }

    // Geocode the address to get real coordinates
    let lat = 49.6114; // fallback: Luxembourg City center (inside LuxVille-BXT)
    let lon = 6.1299;
    try {
      const geo = await axios.get('https://nominatim.openstreetmap.org/search', {
        params: {
          postalcode: appointment.address.zip_code,
          country: appointment.address.country_iso || 'LU',
          format: 'json',
          limit: 1,
        },
      });
      if (geo.data?.length > 0) {
        lon = parseFloat(geo.data[0].lon);
        lat = parseFloat(geo.data[0].lat);
      }
    } catch {
      // Use fallback coordinates
    }

    const payload: any = {
      slot: slotISO,
      prescriptions,
      locale: lang,
      coordInfo: {
        display_name: [appointment.address.number, appointment.address.street, appointment.address.city_name].filter(Boolean).join(', '),
        street_name: appointment.address.street || '',
        street_number: appointment.address.number || '',
        city: appointment.address.city_name || '',
        country: appointment.address.country_iso || 'LU',
        country_code: (appointment.address.country_iso || 'LU').toUpperCase(),
        postcode: appointment.address.zip_code || '',
        state: appointment.address.city_name || '',
        lat,
        lon,
      },
    };

    if (appointment.comment) {
      payload.coordInfo.comment = appointment.comment;
    }

    const { data } = await axiosMYLAB_V2.post(this.apiConfig.APPOINTMENTS_V2.POST_APPOINTMENT(lang), payload);

    this.log(`[V2] bookAppointment()`);

    return data;
  }

  /**
   * Get appointments for a patient
   * New API: GET /api/v4/{lang}/external/myrdv/appointment/get-appointment-by-patient
   * Response: { appointments: [...], pagination: { start, size, totalCount, totalPages } }
   * Adapts → old flat array format
   */
  async getAppointments(patientId: number) {
    const lang = this.getLang();
    const { data } = await axiosMYLAB_V2.get(this.apiConfig.APPOINTMENTS_V2.GET_APPOINTMENTS(lang), {
      params: {
        patientId,
        orderDir: 'ASC',
        size: 100,
        start: 1,
      },
    });

    this.log(`[V2] getAppointments()`);

    // Adapt new paginated format → old flat array
    const appointments = data.appointments ?? data;
    return Array.isArray(appointments)
      ? appointments.map((appt: any) => ({
          id: appt.id,
          patients: appt.prescriptions?.map((p: any) => ({
            formatted_name: p.patient
              ? `${p.patient.mylabId ?? ''}`
              : null,
          })) ?? [],
          address: appt.location
            ? {
                type: 'home',
                number: appt.location.street_number ?? '',
                street: appt.location.street_name ?? '',
                complement: '',
                city_name: appt.location.city ?? '',
                company: '',
                zip_code: appt.location.postcode ?? '',
                country_iso: appt.location.country_code ?? '',
              }
            : appt.center
              ? { type: 'center', number: '', street: appt.center.name ?? appt.center.address ?? '', complement: '', city_name: '', company: '', zip_code: '', country_iso: '' }
              : { type: 'home', number: '', street: '', complement: '', city_name: '', company: '', zip_code: '', country_iso: '' },
          date_time_from: appt.start ?? appt.estimatedStart ?? '',
        }))
      : [];
  }

  /**
   * Cancel an appointment
   * New API: POST /api/v4/{lang}/external/myrdv/appointment/delete
   * Request: { appointIds: [id] }
   */
  async cancelAppointment(id: number) {
    const lang = this.getLang();
    await axiosMYLAB_V2.post(this.apiConfig.APPOINTMENTS_V2.CANCEL_APPOINTMENT(lang), {
      appointmentId: [id],
    });

    this.log(`[V2] cancelAppointment(${id})`);
  }

  /**
   * Get available centers
   * New API: POST /api/v4/{lang}/external/myrdv/center/get-available-centers
   */
  async getAvailableCenters(medicalActIds: number[], coord?: [number, number], radius?: number) {
    const lang = this.getLang();
    const { data } = await axiosMYLAB_V2.post(this.apiConfig.APPOINTMENTS_V2.GET_NEAREST_CENTERS(lang), {
      medical_acts: medicalActIds.map((id) => ({ id })),
      ...(coord ? { coord } : {}),
      ...(radius ? { radius } : {}),
    });

    this.log(`[V2] getAvailableCenters()`);
    return data;
  }

  /**
   * Get available slots for a specific center
   * New API: POST /api/v4/{lang}/external/myrdv/center/get-available-slots
   */
  async getCenterSlots(centerId: number, start: string, end: string, medicalActIds: number[]) {
    const lang = this.getLang();
    const { data } = await axiosMYLAB_V2.post(this.apiConfig.APPOINTMENTS_V2.GET_CENTER_SLOTS(lang), {
      centerId,
      start,
      end,
      medical_acts: medicalActIds.map((id) => ({ id })),
    });

    this.log(`[V2] getCenterSlots()`);

    // Adapt to AppointmentSlots format
    const slots: { [date: string]: string[] } = {};
    let slotDuration = 'PT00H30M';

    Object.entries(data as Record<string, { start: string; end: string; available: boolean }[]>).forEach(([date, dateSlots]) => {
      const availableSlots = dateSlots.filter((s: any) => s.available);
      if (availableSlots.length > 0) {
        slots[date] = availableSlots.map((s: any) => {
          const startTime = new Date(s.start);
          return `${startTime.getHours()}:${startTime.getMinutes().toString().padStart(2, '0')}`;
        });

        if (availableSlots[0]) {
          const startD = new Date(availableSlots[0].start);
          const endD = new Date(availableSlots[0].end);
          const diffMinutes = Math.round((endD.getTime() - startD.getTime()) / 60000);
          const hours = Math.floor(diffMinutes / 60);
          const minutes = diffMinutes % 60;
          slotDuration = `PT${hours.toString().padStart(2, '0')}H${minutes.toString().padStart(2, '0')}M`;
        }
      }
    });

    return {
      pandemic: false,
      slot_duration: slotDuration,
      slot_step_duration: slotDuration,
      slots,
    } as AppointmentSlots;
  }

  /**
   * Get all centers
   * New API: GET /api/v4/{lang}/external/myrdv/center/get-all
   */
  async getAllCenters() {
    const lang = this.getLang();
    const { data } = await axiosMYLAB_V2.get(this.apiConfig.APPOINTMENTS_V2.GET_ALL_CENTERS(lang));

    this.log(`[V2] getAllCenters()`);
    return data;
  }
}

export default (apiConfig: APIConfiguration) => {
  return new AppointmentV2Service(apiConfig);
};
